'use client';

import { useEffect } from 'react';
import { initScrollTracking, initTimeTracking } from '@/lib/analytics';

interface EngagementTrackerProps {
  contentType: 'blog' | 'tool' | 'knowledge_base' | 'service';
  slug: string;
}

/**
 * Client-side engagement tracking component
 *
 * Tracks:
 * - Scroll depth (25%, 50%, 75%, 90%)
 * - Time on page (30s, 60s, 120s, 300s)
 *
 * Usage:
 * ```tsx
 * <EngagementTracker contentType="service" slug="cloud-infrastructure-management" />
 * ```
 */
export default function EngagementTracker({ contentType, slug }: EngagementTrackerProps) {
  useEffect(() => {
    // Initialize scroll tracking
    const cleanupScroll = initScrollTracking();

    // Initialize time tracking
    const cleanupTime = initTimeTracking(contentType, slug);

    // Cleanup both tracking mechanisms on unmount
    return () => {
      cleanupScroll();
      cleanupTime();
    };
  }, [contentType, slug]);

  // This component doesn't render anything
  return null;
}
